/**
 * 1. 每个页面存在必需权限和可选权限, 不满足必需权限的页面无法访问
 * 2. 每个页面继承父级页面的必需权限
 * 3. 组件内使用 v-permission 检测的权限必须位于页面的权限表中
 * 4. 权限可以被重命名, 省略 module 时的自动补全被视为隐性重命名
 * 5. 设置为 public 的页面及其子页面会跳过权限检查, 但在页面内依然可以使用 v-permission
 *
 * rights: {
 *   module: 'moduleA', // if right not include '/', it will wrap with module/right
 *   required: [
 *     'foo', // moduleA/foo
 *     'moduleB/bar' // moduleB/bar
 *   ],
 *   optional: {
 *     'module/foo': 'bar', // v-premission='bar' Yes, v-premission='module/foo' No!
 *     [MyModule.foo]: 'foo' // recommend
 *   }
 * }
 *
 * right: ['moduleA/foo', 'moduleB/bar'] // if right is array or string, they are required
 */

import {
  all,
  complement,
  compose,
  concat,
  map,
  mapObjIndexed,
  mergeAll,
  mergeDeepRight,
  isNil,
  invertObj,
  toPairs
} from 'ramda'
import { RouteConfig } from 'vue-router'
import { routes } from '@/router/routes'
import { get } from '@/api/session/right'
import { notify } from '@/utils/notification'
import { AnyObject } from '@/models/helpers/common'
import { hasChildren, getChildren } from '@/router/tools'

/** Route.meta.rights */
type Rights =
  | string
  | string[]
  | {
      module?: string
      required?: SubRight
      optional?: SubRight
    }

type SubRight = string | string[] | AnyObject | undefined

type Standardization = {
  required: AnyObject
  optional: AnyObject
}

/** key -> rename -> right */
type RightMap = Map<string, RenameMap>
type RenameMap = Map<string, string>

const ownRights = new Set<string>()
const allRightMap: RightMap = new Map()
const requiredMap: RightMap = new Map()
const optionalMap: RightMap = new Map()

// helpers
const emptyMap = new Map<string, string>()
const allNotNil = all(complement(isNil))
const allOwn = all(ownRights.has.bind(ownRights))
/** right -> rename, need invert */
const getRights = (route: RouteConfig): Rights => route!.meta!.rights || {}
const getStandardRights = compose(standard, getRights)
const toInvertPairs = compose(toPairs, invertObj)

/**
 * 检查是否拥有访问页面的所有必需权限
 * @param key route.meta.key
 */
export function hasRight(key: string): boolean
/**
 * 检查是否拥有页面的某权限
 * @param key route.meta.key
 * @param rights 权限名
 */
export function hasRight(key: string, rights: string | string[]): boolean
export function hasRight(key: string, rights?: string | string[]) {
  if (!requiredMap.has(key)) return false

  const rightMap = requiredMap.get(key) || emptyMap
  const getRight = rightMap.get.bind(rightMap)

  const need = isNil(rights)
    ? Array.from(rightMap.values())
    : Array.isArray(rights)
    ? map(getRight, rights)
    : [getRight(rights)]

  return allNotNil(need) && allOwn(need as string[])
}

export async function init() {
  cleanOwnPermisson()
  const rights = await fetchRight()
  map(ownRights.add.bind(ownRights), rights)
  addRights({ required: {}, optional: {} })(routes as any)
}

/**
 * 清空当前账号的权限信息
 */
export function cleanOwnPermisson() {
  ownRights.clear()
}

async function fetchRight() {
  try {
    return await get()
  } catch (e) {
    notify('获取权限信息失败, 请刷新页面或联系管理员', e)
    return []
  }
}

function addRights(pre: Standardization) {
  // TODO testable
  return map((child: RouteConfig) => {
    const rights = getStandardRights(child)
    const merged = mergeDeepRight(pre, rights) as Standardization

    const required = toInvertPairs(merged.required)
    const optional = toInvertPairs(merged.required)
    requiredMap.set(child!.meta!.key, new Map(required))
    optionalMap.set(child!.meta!.key, new Map(optional))
    allRightMap.set(child!.meta!.key, new Map(concat(optional, required)))

    if (hasChildren(child)) addRights(merged)(getChildren(child))
  })
}

/**
 * 格式化权限表, 并补全 module
 * @param rights 未格式化的权限表
 */
export function standard(rights: Rights): Standardization {
  // TODO: too long! split it
  const tmp = (rights as any).module
  const module = tmp ? (tmp.endsWith('/') ? tmp : tmp.concat('/')) : ''
  const getFullRight = (a: string) => (a.includes('/') ? a : module.concat(a))

  const wrapObj = (value: string) => ({ [value]: value })
  const wrapStandard = (required: AnyObject) => ({ required, optional: {} })
  const mapwrapobj = map(wrapObj)
  const wrapArray = compose(mergeAll, mapwrapobj)
  const typeSwitch = (sub: SubRight) =>
    typeof sub === 'string'
      ? wrapObj(sub)
      : Array.isArray(sub)
      ? wrapArray(sub)
      : sub || {}

  const rightCompletion = compose(
    invertObj,
    mapObjIndexed(getFullRight),
    invertObj
  )
  const subStanard = compose(rightCompletion, typeSwitch)

  if (typeof rights === 'string') {
    rights = wrapStandard({ [rights]: rights })
  }

  if (Array.isArray(rights)) {
    rights = compose(
      wrapStandard,
      mergeAll,
      map((a: string) => ({ [a]: a }))
    )(rights)
  }

  return {
    required: subStanard(rights.required),
    optional: subStanard(rights.optional)
  }
}
